import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  BeakerIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline'
import type { NavGroup } from '@/types/navigation.types'

/**
 * Sidebar navigation config.
 * Labels are i18n keys resolved at render time via $t().
 * Groups are separated by dividers in the sidebar.
 * Visibility is controlled by the `access_levels` array on each item/group.
 *
 * Group order (frontend-shell plan, 2026-06-04):
 *   main → workspace → knowledge-base → quality → analytics
 *
 * Add domain-entity items here as you build them — see
 * docs/guides/adding-entities.md → "Register in Sidebar Navigation".
 */
const ALL_LEVELS = ['access_level.admin', 'access_level.user'] as const

export const navigationGroups: NavGroup[] = [
  {
    id: 'main',
    access_levels: [...ALL_LEVELS],
    items: [
      {
        routeName: 'metrics',
        label: 'nav.home',
        icon: ChartBarIcon,
        access_levels: [...ALL_LEVELS],
      },
    ],
  },
  {
    id: 'workspace',
    access_levels: [...ALL_LEVELS],
    items: [
      {
        // Carries the live KB Assistant badge: every pending proposal is
        // reviewed inside this editor, so the outstanding count belongs on
        // the entry point people actually use to act on it.
        routeName: 'kb-editor-entry',
        label: 'nav.kb_editor',
        icon: PencilSquareIcon,
        access_levels: [...ALL_LEVELS],
        badge: 'kbAssistantPending',
      },
      {
        // The KB Assistant summary.
        //
        // It is a dashboard, not a queue (2026-09-09): every verdict is given in
        // the Knowledge Base editor, including for the two kinds that own no row
        // — they are listed there as "in attesa" entries.
        routeName: 'kb-assistant',
        label: 'nav.kb_assistant',
        icon: UserGroupIcon,
        access_levels: [...ALL_LEVELS],
      },
      {
        routeName: 'test-staging',
        label: 'nav.sandbox',
        icon: ChatBubbleBottomCenterTextIcon,
        access_levels: [...ALL_LEVELS],
      },
    ],
  },
  // 'knowledge-base' group (Versioni KB / Topic KB / Paragrafi KB) was
  // removed from the sidebar 2026-06-08 — list views are still reachable
  // via deep-link (e.g. from the audit log) but the operator's main
  // surface is the KB Editor itself. Re-adding voices here is the
  // shortest path if we change our minds.
  {
    id: 'quality',
    access_levels: [...ALL_LEVELS],
    items: [
      {
        routeName: 'evaluation',
        label: 'nav.evaluation',
        icon: BeakerIcon,
        access_levels: [...ALL_LEVELS],
      },
    ],
  },
  {
    // Admin-only (2026-06-21): conversations review is gated to admins, both
    // here (sidebar visibility) and on the route meta + server-side.
    id: 'analytics',
    access_levels: ['access_level.admin'],
    items: [
      {
        // Consolidated 2026-06-16: "Conversations review" (§5.6) now points to
        // the session-review view; the old /conversations page is retired
        // (redirects to /session-review). The old "Review escalation" entry
        // (mislabeled, pointed here) is removed — re-add for §5.7 when built.
        routeName: 'session-review-list',
        label: 'nav.conversations',
        icon: ChatBubbleLeftRightIcon,
        access_levels: ['access_level.admin'],
      },
    ],
  },
  {
    // Admin-only "Profile & Access Management" — the /auth-user list. Gated to
    // admins both here (sidebar visibility) and on the route meta + server-side.
    id: 'admin',
    access_levels: ['access_level.admin'],
    items: [
      {
        routeName: 'users-list',
        label: 'nav.users',
        icon: UsersIcon,
        access_levels: ['access_level.admin'],
      },
    ],
  },
]
